import React from 'react';
import { Dropdown } from 'freemium-ui';
import { get } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { WorkFlowActionOptionType } from 'routes/screens/wflow/types';
import { WorkflowActionType, WorkflowType } from 'shared/types';
import { convertStringWithTagsToQuillHTML } from 'shared/utils';
import { getWorkFlowActionOptions } from '../../utils';
import AddTags from './add-tags';
import SendAnEmail from './send-an-email';
import TriggerWebhook from './trigger-webhook';
import UpdateContact from './update-contact';

type ActionProps = {
  disabled: boolean;
  actionIndex: number;
};

const Action = (props: ActionProps) => {
  const { disabled, actionIndex } = props;
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    formState: { errors, submitCount },
  } = useFormContext<WorkflowType>();

  const event = watch('event');
  const actions = watch('actions');
  const action_type = watch(`actions.${actionIndex}.action_type`);

  const workFlowActionOptions = React.useMemo(
    () =>
      getWorkFlowActionOptions({ event, actions, action_type }).map((item) => ({
        ...item,
        label: t(item.label),
      })),
    [event, t],
  );

  return (
    <div style={{ flex: '1' }}>
      <Dropdown
        placeholder={`${t('forms.placeholders.select')}`}
        defaultSelectedItem={
          workFlowActionOptions.find((item) => item.id === action_type) || null
        }
        emptyItemsText={`${t('no_results_found')}`}
        label={t('workflow.action.action_type.title') || ''}
        width="320px"
        itemToString={(s: WorkFlowActionOptionType) => s.label || ''}
        itemComponent={(s: WorkFlowActionOptionType) => s.label || ''}
        items={workFlowActionOptions}
        disabled={disabled}
        onChange={(item: WorkFlowActionOptionType) => {
          if (item.id === action_type) return;
          const actionToUpdate: WorkflowActionType = {
            action_type: item.id,
          };

          switch (item.id) {
            case 'WEBHOOK':
              actionToUpdate.action_meta = {
                authentication: 'None',
                payload: convertStringWithTagsToQuillHTML(
                  JSON.stringify(
                    {
                      survey_name: '{{survey.name}}',
                      contact_first_name: '{{contact.first_name}}',
                      contact_last_name: '{{contact.last_name}}',
                      contact_email: '{{contact.work_email}}',
                      nps_rating: '{{response.nps_rating}}',
                      nps_rating_group: '{{response.nps_rating_group}}',
                      csat_rating: '{{response.csat_rating}}',
                      csat_rating_group: '{{response.csat_rating_group}}',
                      ces_rating: '{{response.ces_rating}}',
                      ces_rating_group: '{{response.ces_rating_group}}',
                      response_comment: '{{response.comment}}',
                      response_created_at: '{{response.created_at}}',
                      response_channel: '{{response.channel}}',
                    },
                    null,
                    4,
                  ),
                ),
              };
              break;
            case 'UPDATE_A_CONTACT':
              actionToUpdate.action_meta = {
                contact_fields: [{}],
              };
              break;
            case 'SEND_AN_EMAIL':
            case 'SEND_AN_EMAIL_TO_RESPONDENT':
              actionToUpdate.action_meta = {
                delay: 0,
                delay_type: 'Minutes',
              };
              break;
            default:
          }

          setValue(`actions.${actionIndex}`, actionToUpdate, {
            shouldValidate: submitCount > 0,
          });
        }}
        isErrored={
          submitCount > 0 &&
          !!get(errors, `actions.${actionIndex}.action_type.message`)
        }
        errorText={get(
          errors,
          `actions.${actionIndex}.action_type.message`,
          '',
        )}
      />
      {action_type === 'WEBHOOK' && (
        <TriggerWebhook actionIndex={actionIndex} disabled={disabled} />
      )}
      {action_type === 'UPDATE_A_CONTACT' && (
        <UpdateContact actionIndex={actionIndex} disabled={disabled} />
      )}
      {action_type === 'SEND_AN_EMAIL' && (
        <SendAnEmail actionIndex={actionIndex} disabled={disabled} />
      )}
      {action_type === 'SEND_AN_EMAIL_TO_RESPONDENT' && (
        <SendAnEmail
          actionIndex={actionIndex}
          toRespondent
          disabled={disabled}
        />
      )}
      {action_type === 'ADD_TAGS' && (
        <AddTags actionIndex={actionIndex} disabled={disabled} />
      )}
    </div>
  );
};

export default Action;
